export const ProcessOptions = [
  'CUT',
  'CUT-MANUAL/FAB',
  'CUT-WELD',
  'CUT-NOTCH',
  'CUT-NOTCH-CNC',
  'CUT-CNC',
  'CNC',
  'CNC-WELD',
  'MANUAL/FAB-WELD',
  'MANUAL/FAB',
  'NOTCH',
  'WELD',
  'CUT-CNC-WELD',
  'CUT-CNC-MANUAL/FAB',
  'CUT-NOTCH-MANUAL/FAB',
  'CNC-MANUAL/FAB',
] as const;
export type ProcessOptionsType = (typeof ProcessOptions)[number];

export const ProcessStepOptions = [
  'Completed',
  'CUT',
  'CNC',
  'NOTCH',
  'WELD',
  'MANUAL/FAB',
] as const;
export type ProcessStepOptionsType = (typeof ProcessStepOptions)[number];

export const actionTypeOptions = {
  CREATE_WO: 'Created Work Order',
  CREATE_DEFICIENCY_WO: 'Created Deficiency Work Order',
  COMPLETE_STEP: 'Completed Step',
  COMPLETE_PARTS: 'Completed Parts',
  DEFICIENCY_ENTRY: 'Deficiency Entry',
  UPDATE: 'Update',
} as const;
export type PartActionTypeOptions = keyof typeof actionTypeOptions;

export const machineActions = [
  'can_cut',
  'can_weld',
  'can_notch',
  'can_manual_fab',
  'can_cnc',
] as const;
export type MachineActions = typeof machineActions;
export type MachineAction = MachineActions[number];

export const machineActionsPermissions = {
  can_cut: 'Can Cut',
  can_weld: 'Can Weld',
  can_notch: 'Can Notch',
  can_manual_fab: 'Can Manual Fab',
  can_cnc: 'Can CNC',
} as const;
export type MachineActionsPermissionsKey =
  keyof typeof machineActionsPermissions;

export const relTypes = ['frame', 'bulk', 'buyout'] as const;
export type ReleaseType = (typeof relTypes)[number];

export const scopes = ['standard', 'pmu', 'vmu'] as const;
export type Scope = (typeof scopes)[number];

export const teams = ['karas', 'partner'] as const;
export type Team = (typeof teams)[number];

// Presigned URLs modules
export const ATTACHMENTS_PRESIGNED_URL_MODULES = [
  'task',
  'rfi',
  'rfi_log',
  'submittal',
  'rfi_submittal_return',
  'quality_control',
  'invoice',
  'frame_masterfiles',
  'part_masterfiles',
  'bom',
  'frame_qty',
  'budget_imports',
  'po_import',
  'receiving_import',
  'receiving_delivery',
  'site_delivery',
] as const;

// enums
type EnumProps = {
  label: string;
  value: string;
};

// on hold receipts update options
export const onHoldReceiptUpdateEnums = [
  {
    label: 'On Hold Qty (Absolute Change)',
    value: 'on_hold_qty',
  },
  {
    label: 'Delta (Increment/ Decrement)',
    value: 'delta',
  },
] as const satisfies EnumProps[];

// draft order statuses
export const draftOrderStatusEnums = [
  {
    label: 'Draft',
    value: 'DRAFT',
  },
  {
    label: 'Converted',
    value: 'CONVERTED',
  },
  {
    label: 'Archived',
    value: 'ARCHIVED',
  },
] as const satisfies EnumProps[];

// purchase order statuses
export const purchaseOrderStatusEnums = [
  // received from backend
  {
    label: 'Cancelled',
    value: 'CANCELLED',
  },
  {
    label: 'Draft PO',
    value: 'DRAFT_PO',
  },
  // purchase order editable statuses. users can change between these statuses only
  {
    label: 'Issued',
    value: 'ISSUED',
  },
  {
    label: 'Confirmed',
    value: 'CONFIRMED',
  },
  {
    label: 'Partially Received',
    value: 'PARTIALLY_RECEIVED',
  },
  {
    label: 'Received',
    value: 'RECEIVED',
  },
  {
    label: 'Closed',
    value: 'CLOSED',
  },
] as const satisfies EnumProps[];
export type PurchaseOrderStatusType =
  (typeof purchaseOrderStatusEnums)[number]['label'];

// work order steps
export const WORK_ORDER_STEPS = [
  'ASSEMBLY',
  'GLAZING',
  'PACKING',
  'SHIPPING',
  'INSTALLATION',
] as const;

export const workOrderHistoryEnums = [
  {
    label: 'Assembly',
    value: 'ASSEMBLY',
  },
  {
    label: 'Glazing',
    value: 'GLAZING',
  },
  {
    label: 'Packing',
    value: 'PACKING',
  },
  {
    label: 'Shipping',
    value: 'SHIPPING',
  },
  {
    label: 'Installation',
    value: 'INSTALLATION',
  },
  {
    label: 'Work Order Created',
    value: 'WORK_ORDER_CREATED',
  },
] as const satisfies EnumProps[];

export const taskOrderEnums = [
  {
    label: 'Pending',
    value: 'PENDING',
  },
  {
    label: 'In Progress',
    value: 'IN_PROGRESS',
  },
  {
    label: 'Completed',
    value: 'COMPLETED',
  },
] as const satisfies EnumProps[];

export const workOrderStatus = [
  {
    label: 'Pending',
    value: 'PENDING',
  },
  {
    label: 'In Progress',
    value: 'IN_PROGRESS',
  },
  {
    label: 'Completed',
    value: 'COMPLETED',
  },
  {
    label: 'On Hold',
    value: 'ON_HOLD',
  },
] as const satisfies EnumProps[];
export type WorkOrderStatusType = (typeof workOrderStatus)[number]['value'];

// rfi status
export const rfiStatus = [
  {
    label: 'Open',
    value: 'open',
  },
  {
    label: 'Closed',
    value: 'closed',
  },
] as const satisfies EnumProps[];

// rfi priority
export const rfiPriority = [
  {
    label: 'Blocking',
    value: 'blocking',
  },
  {
    label: 'High',
    value: 'high',
  },
  {
    label: 'Delays',
    value: 'delays',
  },
  {
    label: 'Normal',
    value: 'normal',
  },
] as const satisfies EnumProps[];

// rfi impact
export const rfiImpact = [
  {
    label: 'High',
    value: 'high',
  },
  {
    label: 'Medium',
    value: 'medium',
  },
  {
    label: 'Low',
    value: 'low',
  },
] as const satisfies EnumProps[];

// rfi-log enums
export const rfiLogStatus = [
  {
    label: 'Draft',
    value: 'draft',
  },
  {
    label: 'Submitted',
    value: 'submitted',
  },
  {
    label: 'Resubmittal Required',
    value: 'resubmittal_required',
  },
  {
    label: 'Closed',
    value: 'closed',
  },
  {
    label: 'Void',
    value: 'void',
  },
] as const satisfies EnumProps[];

export const rfiLogPriority = [
  {
    label: 'Blocking',
    value: 'blocking',
  },
  {
    label: 'High',
    value: 'high',
  },
  {
    label: 'Delays',
    value: 'delays',
  },
  {
    label: 'Normal',
    value: 'normal',
  },
  {
    label: 'Low',
    value: 'low',
  },
] as const satisfies EnumProps[];

export const rfiLogImpact = [
  {
    label: 'High',
    value: 'high',
  },
  {
    label: 'Medium',
    value: 'medium',
  },
  {
    label: 'Low',
    value: 'low',
  },
  {
    label: 'None',
    value: 'none',
  },
] as const satisfies EnumProps[];

// submittals enums
export const submittalStatus = [
  {
    label: 'Draft',
    value: 'draft',
  },
  {
    label: 'Submitted',
    value: 'submitted',
  },
  {
    label: 'Resubmittal Required',
    value: 'resubmittal_required',
  },
  {
    label: 'Closed',
    value: 'closed',
  },
  {
    label: 'Void',
    value: 'void',
  },
] as const satisfies EnumProps[];

export const submittalType = [
  {
    label: 'Shop Drawing',
    value: 'shop_drawing',
  },
  {
    label: 'Product Data',
    value: 'product_data',
  },
  {
    label: 'Sample',
    value: 'sample',
  },
  {
    label: 'Test Report',
    value: 'test_report',
  },
  {
    label: 'Calculation',
    value: 'calculation',
  },
  {
    label: 'Warranty',
    value: 'warranty',
  },
  {
    label: 'O and M',
    value: 'o_and_m',
  },
  {
    label: 'Other',
    value: 'other',
  },
] as const satisfies EnumProps[];

// quality control
export const qualityControlModuleEnums = [
  {
    label: 'Assembly',
    value: 'ASSEMBLY',
  },
  {
    label: 'Glazing',
    value: 'GLAZING',
  },
  {
    label: 'Installation',
    value: 'INSTALLATION',
  },
  {
    label: 'Packing',
    value: 'PACKING',
  },
  {
    label: 'Shipping',
    value: 'SHIPPING',
  },
  {
    label: 'Receiving',
    value: 'RECEIVING',
  },
  {
    label: 'Fabrication',
    value: 'FABRICATION',
  },
] as const satisfies EnumProps[];
export type QualityControlModule =
  (typeof qualityControlModuleEnums)[number]['value'];

export const qualityControlSeverityEnums = [
  {
    label: 'Low',
    value: 'LOW',
  },
  {
    label: 'Medium',
    value: 'MEDIUM',
  },
  {
    label: 'High',
    value: 'HIGH',
  },
  {
    label: 'Critical',
    value: 'CRITICAL',
  },
] as const satisfies EnumProps[];
export type QualityControlSeverity =
  (typeof qualityControlSeverityEnums)[number]['value'];

export const qualityControlDispositionEnums = [
  {
    label: 'Hold',
    value: 'HOLD',
  },
  {
    label: 'Rework',
    value: 'REWORK',
  },
  {
    label: 'Scrap',
    value: 'SCRAP',
  },
  {
    label: 'Use As Is',
    value: 'USE_AS_IS',
  },
  {
    label: 'Return to Vendor',
    value: 'RETURN_VENDOR',
  },
] as const satisfies EnumProps[];
export type QualityControlDisposition =
  (typeof qualityControlDispositionEnums)[number]['value'];

export const qualityControlRootCauseCategoryEnums = [
  {
    label: 'Process',
    value: 'PROCESS',
  },
  {
    label: 'Material',
    value: 'MATERIAL',
  },
  {
    label: 'Equipment',
    value: 'EQUIPMENT',
  },
  {
    label: 'Design',
    value: 'DESIGN',
  },
  {
    label: 'Handling',
    value: 'HANDLING',
  },
  {
    label: 'Vendor',
    value: 'VENDOR',
  },
  {
    label: 'Install',
    value: 'INSTALL',
  },
  {
    label: 'Other',
    value: 'OTHER',
  },
] as const satisfies EnumProps[];
export type QualityControlRootCauseCategory =
  (typeof qualityControlRootCauseCategoryEnums)[number]['value'];

export const qualityControlStatusEnums = [
  {
    label: 'Open',
    value: 'OPEN',
  },
  {
    label: 'Closed',
    value: 'CLOSED',
  },
] as const satisfies EnumProps[];
export type QualityControlStatus =
  (typeof qualityControlStatusEnums)[number]['value'];

export const qualityControlResultEnums = [
  {
    label: 'Pass',
    value: 'PASS',
  },
  {
    label: 'Fail',
    value: 'FAIL',
  },
  {
    label: 'Info',
    value: 'INFO',
  },
] as const satisfies EnumProps[];
export type QualityControlResult =
  (typeof qualityControlResultEnums)[number]['value'];
